import React, { ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { useIsPaused } from 'store/config/selectors'
import { useCurrentCount } from 'store/workout/selectors'
import './counter.scss'

export enum CounterSizes {
  XSMALL = 'xs',
  SMALL = 'sm',
  MEDIUM = 'md',
  LARGE = 'lg',
  XLARGE = 'xl',
}

interface CounterProps {
  currentCount: number
  initCount?: number
  size?: CounterSizes
  textEl?: ReactNode
  endCounter?: () => void
  isTimeInMin?: boolean
}

interface CurrentCounterProps extends Omit<CounterProps, 'currentCount'> {
  snapToInitOnMount?: boolean
}

interface DynamicSize {
  height?: string
  secSize?: string
  fontSize?: string
  secTop?: string
  subTop?: string
  subFontSize?: string
  borderWidth?: string
}

const COUNTER_FRAME_INTERVAL_MS = 50

const getCountMs = (count: number) => Math.max(0, count * 1000)

export const Counter = ({ size, currentCount, initCount, endCounter, textEl, isTimeInMin = false }: CounterProps) => {
  const sizeObj: DynamicSize = {}
  switch (size) {
    case CounterSizes.XSMALL:
      sizeObj.borderWidth = '7px'
      sizeObj.height = '150px'
      sizeObj.fontSize = '70px'
      sizeObj.secSize = '30px'
      sizeObj.secTop = '40px'
      sizeObj.subFontSize = '60px'
      sizeObj.subTop = '100px'
      break
    case CounterSizes.SMALL:
      sizeObj.borderWidth = '20px'
      sizeObj.height = '320px'
      sizeObj.fontSize = '120px'
      sizeObj.secSize = '40px'
      sizeObj.secTop = '60px'
      sizeObj.subFontSize = '60px'
      sizeObj.subTop = '120px'
      break
    case CounterSizes.MEDIUM:
      sizeObj.borderWidth = '20px'
      sizeObj.height = '430px'
      sizeObj.fontSize = '160px'
      sizeObj.secSize = '60px'
      sizeObj.secTop = '120px'
      sizeObj.subFontSize = '60px'
      sizeObj.subTop = '220px'
      break
    case CounterSizes.LARGE:
      sizeObj.borderWidth = '30px'
      sizeObj.height = '500px'
      sizeObj.fontSize = '160px'
      sizeObj.secSize = '60px'
      sizeObj.secTop = '135px'
      sizeObj.subFontSize = '60px'
      sizeObj.subTop = '240px'
      break
    case CounterSizes.XLARGE:
      sizeObj.borderWidth = '30px'
      sizeObj.height = '560px'
      sizeObj.fontSize = '160px'
      sizeObj.secSize = '60px'
      sizeObj.secTop = '150px'
      sizeObj.subFontSize = '60px'
      sizeObj.subTop = '260px'
      break
  }

  const sizeStyle = {
    '--size': sizeObj.height,
    '--fontsize': sizeObj.fontSize,
    '--sectop': sizeObj.secTop,
    '--secsize': sizeObj.secSize,
    '--subtop': sizeObj.subTop,
    '--borderwidth': sizeObj.borderWidth,
  } as React.CSSProperties

  const isPaused = useIsPaused()
  const [msLeft, setMsLeft] = useState<number>(getCountMs(currentCount)) // using ms instead of s will make the counter more consistent. When a lot of hits or other async functions are coming in, they seem to push the timer operation in the callback queue, resulting in inconsistent seconds.

  useLayoutEffect(() => {
    const nextMsLeft = getCountMs(currentCount)
    setMsLeft(nextMsLeft)
  }, [currentCount])

  useEffect(() => {
    if (isPaused || currentCount <= 0) return

    let animationFrameId: number | null = null
    let lastFrameMs = 0
    const startMs = window.performance.now()
    const startMsLeft = getCountMs(currentCount)

    const stopCounterFrame = () => {
      if (animationFrameId === null) return
      window.cancelAnimationFrame(animationFrameId)
      animationFrameId = null
    }

    const runCounterFrame = (timestamp: number) => {
      if (timestamp - lastFrameMs >= COUNTER_FRAME_INTERVAL_MS) {
        lastFrameMs = timestamp
        const nextMsLeft = Math.max(0, startMsLeft - (timestamp - startMs))
        setMsLeft(nextMsLeft)
      }

      animationFrameId = window.requestAnimationFrame(runCounterFrame)
    }

    animationFrameId = window.requestAnimationFrame(runCounterFrame)

    return stopCounterFrame
  }, [currentCount, isPaused])

  const ringStyle = useMemo(() => {
    const durationSeconds = Math.max(initCount ?? currentCount, currentCount, 1)
    const progressDegrees = Math.max(0, Math.min(360, (msLeft / (durationSeconds * 1000)) * 360))

    return {
      '--ringOuter': progressDegrees,
      '--ringInner': progressDegrees,
    } as React.CSSProperties
  }, [currentCount, initCount, msLeft])

  const currentValue = isTimeInMin ? Math.floor(currentCount / 60) : currentCount
  const timeUnit = isTimeInMin ? 'Min.' : 'Sec.'

  return (
    <div onClick={endCounter} className="counter" style={{ ...ringStyle, ...sizeStyle }}>
      <div id="ring" className="counter__outer"></div>
      <div className="counter__inner">
        <span id="seconds" className={`counter counter__sec`}>
          {currentValue}
          {<div className="counter__timeUnit">{timeUnit}</div>}
          {textEl}
        </span>
      </div>
    </div>
  )
}

const CurrentCounter = ({
  size = CounterSizes.SMALL,
  endCounter,
  initCount,
  textEl,
  snapToInitOnMount = false,
}: CurrentCounterProps) => {
  const currentCount = useCurrentCount()
  const previousInitCountRef = useRef(initCount)
  const [snapToInit, setSnapToInit] = useState(snapToInitOnMount)

  const initCountChanged = previousInitCountRef.current !== initCount
  if (initCountChanged) {
    previousInitCountRef.current = initCount
  }

  useLayoutEffect(() => {
    if (!snapToInitOnMount || initCount === undefined) return

    setSnapToInit(true)
    const animationFrameId = window.requestAnimationFrame(() => {
      setSnapToInit(false)
    })

    return () => window.cancelAnimationFrame(animationFrameId)
  }, [initCount, snapToInitOnMount])

  const displayedCurrentCount = snapToInitOnMount && initCount !== undefined && (snapToInit || initCountChanged)
    ? initCount
    : currentCount

  return (
    <Counter
      initCount={initCount}
      endCounter={endCounter}
      currentCount={displayedCurrentCount}
      size={size}
      textEl={textEl}
    />
  )
}

export default CurrentCounter
